import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users2,
  Truck,
  MapPin,
  Loader2,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printOperationsReport } from "@/lib/operationsReportPrint";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import { ReportTrendPanel } from "@/components/reports/ReportTrendPanel";
import PaymentMethodFilter from "@/components/reports/PaymentMethodFilter";
import OrderTypeFilter from "@/components/reports/OrderTypeFilter";
import { formatReportFilterLine } from "@/lib/reportFilters";

const TABS = [
  { id: "customers", label: "Customer Wise", path: "/reports/customer-sales", icon: Users2 },
  { id: "delivery-persons", label: "Delivery Person", path: "/reports/delivery-person-sales", icon: Truck },
  { id: "delivery-areas", label: "Delivery Area", path: "/reports/delivery-area-sales", icon: MapPin },
];

const ENDPOINTS = {
  customers: "/reports/operations/customers",
  "delivery-persons": "/reports/operations/delivery-persons",
  "delivery-areas": "/reports/operations/delivery-areas",
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function OperationsReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const activeTab = useMemo(() => {
    if (location.pathname.includes("delivery-person")) return "delivery-persons";
    if (location.pathname.includes("delivery-area")) return "delivery-areas";
    return "customers";
  }, [location.pathname]);

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [orderType, setOrderType] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [personId, setPersonId] = useState("all");
  const [areaId, setAreaId] = useState("all");

  const { data: personsData } = useQuery({
    queryKey: ["delivery-persons", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery-persons", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId && activeTab === "delivery-persons"),
  });

  const { data: areasData } = useQuery({
    queryKey: ["delivery-areas", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery-areas", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId && activeTab === "delivery-areas"),
  });

  const persons = personsData?.delivery_persons ?? [];
  const areas = areasData?.delivery_areas ?? [];

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["ops-report", activeTab, branchId, fromDate, toDate, orderType, paymentMethod, personId, areaId],
    queryFn: async () => {
      const params = { ...reportParams, from_date: fromDate, to_date: toDate };
      if (paymentMethod !== "all") params.payment_method = paymentMethod;
      if (activeTab === "customers" && orderType !== "all") params.order_type = orderType;
      if (activeTab === "delivery-persons" && personId !== "all") {
        params.delivery_person_id = personId;
      }
      if (activeTab === "delivery-areas" && areaId !== "all") {
        params.delivery_area_id = areaId;
      }
      const { data: res } = await api.get(ENDPOINTS[activeTab], { params });
      return res;
    },
    enabled: Boolean(canQuery && fromDate && toDate),
  });

  const title = TABS.find((t) => t.id === activeTab)?.label ?? "Operations Reports";

  const chartData = useMemo(() => {
    return (report?.rows || []).slice(0, 12).map((r) => {
      const name =
        r.customer_name ||
        r.person_name ||
        r.area_name ||
        "—";
      return {
        name: name.length > 16 ? `${name.slice(0, 16)}…` : name,
        net: r.net_sales,
        bills: r.bill_count,
      };
    });
  }, [report]);

  return (
    <div className="min-w-0">
      <PageHeader
        title={title}
        subtitle={
          activeTab === "customers"
            ? "Sales grouped by customer for the selected period"
            : activeTab === "delivery-persons"
              ? "Delivery orders grouped by driver"
              : "Delivery orders grouped by area"
        }
        actions={
          <ReportExportButtons
            disabled={!report}
            title={title}
            filename={`operations-${activeTab}`}
            period={formatReportFilterLine({
              fromDate,
              toDate,
              orderType: activeTab === "customers" ? orderType : "all",
              paymentMethod,
            })}
            onPrint={() => printOperationsReport(report, activeTab, { branchId, allBranches })}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
            <Input type="date" className="h-9 w-[160px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
            <Input type="date" className="h-9 w-[160px]" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <PaymentMethodFilter value={paymentMethod} onChange={setPaymentMethod} />
          {activeTab === "customers" && (
            <OrderTypeFilter value={orderType} onChange={setOrderType} />
          )}
          {activeTab === "delivery-persons" && (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Driver (optional)</Label>
              <Select value={personId} onValueChange={setPersonId}>
                <SelectTrigger className="h-9 w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All drivers</SelectItem>
                  {persons.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.code} — {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {activeTab === "delivery-areas" && (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Area (optional)</Label>
              <Select value={areaId} onValueChange={setAreaId}>
                <SelectTrigger className="h-9 w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  {areas.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>{a.code} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(isLoading || isFetching) && (
            <Pill className="mb-1"><Loader2 size={12} className="animate-spin mr-1" /> Loading…</Pill>
          )}
        </div>
      </Card>

      {report && (
        <>
          <SummaryCards report={report} activeTab={activeTab} />

          {activeTab === "customers" && <CustomerTable rows={report.rows} />}
          {activeTab === "delivery-persons" && (
            <DeliveryTable rows={report.rows} bills={report.bills} type="person" />
          )}
          {activeTab === "delivery-areas" && (
            <DeliveryTable rows={report.rows} bills={report.bills} type="area" />
          )}

          {chartData.length > 0 && (
            <ReportTrendPanel title="Top by net sales (optional)">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => aed(v)} />
                  <Bar dataKey="net" fill="#F36F21" name="Net sales" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ReportTrendPanel>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCards({ report, activeTab }) {
  const s = report.summary || {};
  const items =
    activeTab === "customers"
      ? [
          { label: "Customers", value: num(s.customer_count) },
          { label: "Bills", value: num(s.bill_count) },
          { label: "Gross", value: aed(s.gross_sales) },
          { label: "Discount", value: aed(s.discount) },
          { label: "VAT", value: aed(s.vat_collected) },
          { label: "Round off", value: aed(s.round_off) },
          { label: "Net", value: aed(s.net_sales), emphasize: true },
        ]
      : [
          { label: activeTab === "delivery-persons" ? "Drivers" : "Areas", value: num(s.driver_count ?? s.area_count) },
          { label: "Bills", value: num(s.bill_count) },
          { label: "Parcel charges", value: aed(s.parcel_charges) },
          { label: "Gross", value: aed(s.gross_sales) },
          { label: "VAT", value: aed(s.vat_collected) },
          { label: "Round off", value: aed(s.round_off) },
          { label: "Net", value: aed(s.net_sales), emphasize: true },
        ];

  return <ReportKpiStrip items={items} />;
}

function CustomerTable({ rows }) {
  return (
    <ReportTableCard title="By customer">
      <table>
        <thead>
          <tr>
            <th className="text-left">Code</th>
            <th className="text-left">Customer</th>
            <th className="text-left">TRN</th>
            <th className="text-left">Phone</th>
            <th className="text-right">Bills</th>
            <th className="text-right">Gross</th>
            <th className="text-right">Discount</th>
            <th className="text-right">VAT</th>
            <th className="text-right">Round off</th>
            <th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={`${r.customer_id}-${r.customer_name}-${r.customer_tax_number || ""}`}>
              <td className="font-mono text-[12px]">{r.customer_code || "—"}</td>
              <td>{r.customer_name}</td>
              <td className="font-mono text-[12px]">{r.customer_tax_number || "—"}</td>
              <td>{r.phone || "—"}</td>
              <td className="text-right">{r.bill_count}</td>
              <td className="text-right font-mono">{aed(r.gross_sales)}</td>
              <td className="text-right font-mono">{aed(r.discount)}</td>
              <td className="text-right font-mono">{aed(r.vat_collected)}</td>
              <td className="text-right font-mono">{aed(r.round_off)}</td>
              <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportTableCard>
  );
}

function DeliveryTable({ rows, bills, type }) {
  const isPerson = type === "person";

  return (
    <>
      <ReportTableCard title={isPerson ? "By driver" : "By area"}>
        <table>
          <thead>
            <tr>
              <th className="text-left">Code</th>
              <th className="text-left">{isPerson ? "Driver" : "Area"}</th>
              {!isPerson && <th className="text-right">Charge</th>}
              <th className="text-right">Bills</th>
              <th className="text-right">Parcel</th>
              <th className="text-right">Round off</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.delivery_person_id ?? r.delivery_area_id ?? r.person_name ?? r.area_name}>
                <td className="font-mono text-[12px]">{r.person_code || r.area_code || "—"}</td>
                <td>{r.person_name || r.area_name}</td>
                {!isPerson && (
                  <td className="text-right font-mono">{aed(r.delivery_charge)}</td>
                )}
                <td className="text-right">{r.bill_count}</td>
                <td className="text-right font-mono">{aed(r.parcel_charge)}</td>
                <td className="text-right font-mono">{aed(r.round_off)}</td>
                <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>

      {(bills || []).length > 0 && (
        <ReportTableCard title="Delivery bills">
          <table>
            <thead>
              <tr>
                <th className="text-left">Bill</th>
                <th className="text-left">Time</th>
                <th className="text-left">Customer</th>
                <th className="text-left">TRN</th>
                <th className="text-left">Area</th>
                <th className="text-left">Driver</th>
                <th className="text-right">Parcel</th>
                <th className="text-right">Round off</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id}>
                  <td className="font-mono text-[12px]">{b.bill_number}</td>
                  <td>{b.paid_time}</td>
                  <td>{b.customer_name}</td>
                  <td className="font-mono text-[12px]">{b.customer_tax_number || "—"}</td>
                  <td>{b.delivery_area || "—"}</td>
                  <td>{b.delivery_person || "—"}</td>
                  <td className="text-right font-mono">{aed(b.parcel_charge)}</td>
                  <td className="text-right font-mono">{aed(b.round_off)}</td>
                  <td className="text-right font-mono font-medium">{aed(b.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      )}
    </>
  );
}
